import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingOffice2Icon, ChatBubbleLeftRightIcon, MegaphoneIcon } from '@heroicons/react/24/outline';

import { useStateContext } from '../../../contexts/ContextProvider';
import { enregistrerModeMuet, ouvrirFil, useFilsMenage, type BoiteMenage, type TypeFil } from '../../api/useMessagesMenage';
import Button from '../../components/Button';
import EtatErreur from '../../components/EtatErreur';
import Screen from '../../components/Screen';
import Text from '../../components/Text';
import { heureBulle } from '../../helpers/conversation';
import { apercuMessage, titreFil } from '../../helpers/messagesMenage';
import { t } from '../../i18n/langue';
import EnteteApp from '../../shell/EnteteApp';
import { useCoque } from '../../shell/CoqueContext';

/**
 * Les fils de la messagerie ménage du lecteur, et de quoi en ouvrir un : chaque
 * boîte joignable (Q3, T3), l'équipe du jour pour la gouvernance (Q4), un hôtel
 * du groupe (T4). En bas, le réglage personnel « muet les jours sans travail » (T2).
 */
export default function ListeFils() {
    const navigate = useNavigate();
    const { ouvrirPanneau } = useCoque();
    const { currentFacility, user, setUser } = useStateContext();
    const { data, isLoading, isError, refetch } = useFilsMenage(currentFacility?.id ?? null);
    const [muet, setMuet] = useState<boolean>(() => (user as { hk_quiet_off_duty?: boolean } | null)?.hk_quiet_off_duty ?? true);
    const [ouverture, setOuverture] = useState(false);

    const ouvrir = async (demande: { kind: TypeFil; box?: BoiteMenage; other_facility_id?: number }) => {
        if (ouverture) return;
        setOuverture(true);
        try {
            const fil = await ouvrirFil(demande);
            navigate(`/hk-messages/${fil.id}`);
        } finally {
            setOuverture(false);
        }
    };

    // R7 : l'utilisateur du contexte suit, sinon l'écran rouvrirait sur l'ancienne valeur.
    const basculerMuet = (valeur: boolean) => {
        setMuet(valeur);
        enregistrerModeMuet(valeur)
            .then(() => setUser({ ...user, hk_quiet_off_duty: valeur }))
            .catch(() => setMuet(!valeur));
    };

    const fils = data?.threads ?? [];

    return (
        <Screen entete={<EnteteApp titre={t('messagesMenage.titre')} onOuvrirPanneau={ouvrirPanneau} />}>
            <div className="flex flex-col gap-3 px-4 py-4">
                {data?.ceiling?.reached && (
                    <div role="status" className="rounded-xl bg-white px-3 py-2">
                        <Text variante="petit" ton="danger">
                            {t('messagesMenage.plafond')}
                        </Text>
                    </div>
                )}

                <div className="flex flex-col gap-2">
                    {data?.can.team && (
                        <Button
                            libelle={t('messagesMenage.messageEquipe')}
                            icone={MegaphoneIcon}
                            pleineLargeur
                            disabled={ouverture}
                            onPress={() => void ouvrir({ kind: 'team' })}
                        />
                    )}
                    {data?.can.open_boxes.map((boite) => (
                        <Button
                            key={boite}
                            libelle={t('messagesMenage.ecrireA', { boite: t(`messagesMenage.boites.${boite}`) })}
                            icone={ChatBubbleLeftRightIcon}
                            variante={boite === 'gouvernance' ? 'primaire' : 'secondaire'}
                            pleineLargeur
                            disabled={ouverture}
                            onPress={() => void ouvrir({ kind: 'direct', box: boite })}
                        />
                    ))}
                    {data?.can.inter_facilities.map((hotel) => (
                        <Button
                            key={hotel.id}
                            libelle={hotel.name}
                            icone={BuildingOffice2Icon}
                            variante="secondaire"
                            pleineLargeur
                            disabled={ouverture}
                            onPress={() => void ouvrir({ kind: 'inter_facility', other_facility_id: hotel.id })}
                        />
                    ))}
                </div>

                {isLoading && (
                    <Text variante="petit" ton="attenue">
                        {t('commun.chargement')}
                    </Text>
                )}
                {isError && <EtatErreur onReessayer={() => void refetch()} />}
                {data && fils.length === 0 && (
                    <Text variante="petit" ton="attenue">
                        {t('messagesMenage.aucunFil')}
                    </Text>
                )}

                <ul className="flex flex-col gap-2">
                    {fils.map((fil) => (
                        <li key={fil.id}>
                            {/* mobile-check-ignore — la ligne EST le contrôle, comme la carte de chambre. */}
                            <button
                                type="button"
                                onClick={() => navigate(`/hk-messages/${fil.id}`)}
                                className={`flex min-h-touch w-full items-center gap-3 rounded-xl bg-white px-3 py-3 text-left ${
                                    fil.has_unread_urgent ? 'border-2 border-alert-text-color' : ''
                                }`}
                            >
                                <span className="min-w-0 flex-1">
                                    <Text as="span" className={`block ${fil.unread_count > 0 ? 'font-semibold' : ''}`}>
                                        {fil.has_unread_urgent && '🔴 '}
                                        {titreFil(fil, user?.id)}
                                    </Text>
                                    <Text variante="petit" ton="attenue" as="span" className="block truncate">
                                        {apercuMessage(fil.last_message)}
                                    </Text>
                                </span>
                                <span className="flex shrink-0 flex-col items-end gap-1">
                                    {fil.last_message_at && (
                                        <Text variante="petit" ton="attenue" as="span">
                                            {heureBulle(fil.last_message_at)}
                                        </Text>
                                    )}
                                    {fil.unread_count > 0 && (
                                        <span
                                            aria-label={t('messagesMenage.nonLus', { n: fil.unread_count })}
                                            className="rounded-full bg-main-color px-2 text-sm font-semibold text-white"
                                        >
                                            {fil.unread_count}
                                        </span>
                                    )}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>

                <label className="mt-4 flex min-h-touch items-start gap-3 rounded-xl bg-white px-3 py-3">
                    <input
                        type="checkbox"
                        checked={muet}
                        onChange={(e) => basculerMuet(e.target.checked)}
                        className="mt-1 h-6 w-6 shrink-0 rounded border-border-color-light text-main-color"
                    />
                    <span>
                        <Text as="span" className="block">
                            {t('messagesMenage.muet')}
                        </Text>
                        <Text variante="petit" ton="attenue" as="span" className="block">
                            {t('messagesMenage.muetDetail')}
                        </Text>
                    </span>
                </label>
            </div>
        </Screen>
    );
}
